import React from "react";

import { faEye, faSync } from "@fortawesome/free-solid-svg-icons";
import {
  faCopy,
  faFileEdit,
  faSignOut,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";

import {
  closeConfirmModal,
  openConfirmModal,
  openDispatchingModal,
  RuleAction,
  RuleBookAction,
} from "actions";

import { ColumnType } from "components/AgGrid/constants";
import { MultiButton, SlimButton } from "components/Button";

import { ACTION_COLUMN_ID, ACTION_COLUMN_NAME } from "constants/aggrid";
import {
  LedgerAccountColId,
  LedgerAccountColumnDef,
} from "constants/columnDefinitions/ledgerAccount";
import { MasterRuleColumnDefinitions } from "constants/columnDefinitions/masterRule";
import { RuleColumnDefinitions } from "constants/columnDefinitions/rule";
import { RuleSetColumnDefinitions } from "constants/columnDefinitions/ruleSet";
import { ModalTypes } from "constants/navigation";
import { RuleBookPermissions, RuleSetPermissions } from "constants/permissions";

import { createNamespacedColumnDefinitions, getters } from "lib/agGrid";
import { valueExistsRenderer } from "lib/agGrid/renderers";
import { RuleSyncStatusRenderer } from "lib/agGrid/renderers/statusRenderer";
import { openModalLink } from "lib/navigation";
import { hasPermission } from "lib/permissions";
import { pluralize } from "lib/pluralize";

import {
  getIsStaff,
  getRuleBookById,
  getRulesByRuleBookId,
  getRuleSetById,
  getRuleSetOptionsByRuleBookId,
  selectMasterRulesByMasterRuleBookIdLookup,
} from "selectors";

const { [LedgerAccountColId.ACTIONS]: _ignored, ...LedgerAccountColumns } =
  LedgerAccountColumnDef;

export const ruleColumnDefs = cellRendererOptions => [
  {
    headerName: "Rule",
    children: Object.values(RuleColumnDefinitions),
  },
  {
    headerName: "Master Rule",
    children: Object.values(MasterRuleColumnDefinitions),
  },
  {
    headerName: "Rule Set",
    children: Object.values(RuleSetColumnDefinitions),
  },
  {
    headerName: "Ledger Account",
    children: Object.values(
      createNamespacedColumnDefinitions(LedgerAccountColumns, "ledgerAccount"),
    ),
  },
  {
    headerName: "Status",
    colId: "masterRuleStatus",
    field: "masterRuleStatus",
    cellRenderer: RuleSyncStatusRenderer,
    tooltipValueGetter: getters.ruleSyncTooltipGetter,
  },
  {
    headerName: "Customised",
    colId: "isCustomised",
    field: "isCustomised",
    cellRenderer: valueExistsRenderer,
  },

  {
    colId: ACTION_COLUMN_ID,
    field: "rule.id",
    headerName: ACTION_COLUMN_NAME,
    width: 160,
    type: ColumnType.STRING,
    cellRenderer: RuleActions,
    cellRendererParams: { ...cellRendererOptions },
    pinned: "right",
  },
];

function RuleActions({ data, isInEditRuleSetModal }) {
  const dispatch = useDispatch();

  const { rule = {}, masterRule = {}, masterRuleBook } = data || {};

  const ruleId = rule.id;
  const ruleBookId = rule.rule_book_id;
  const ruleSetId = rule.rule_set_id;

  const isStaff = useSelector(getIsStaff);
  const ruleBook = useSelector(getRuleBookById(ruleBookId));
  const masterRulesByMasterRuleBookIdLookup = useSelector(
    selectMasterRulesByMasterRuleBookIdLookup,
  );
  const rulesBookRules = useSelector(getRulesByRuleBookId(ruleBookId)) || [];
  const ruleSet = useSelector(getRuleSetById(ruleSetId));

  const ruleSetOptions =
    useSelector(getRuleSetOptionsByRuleBookId(ruleBookId)) || [];

  if (!data) {
    return null;
  }

  // Rule permissions are inherited from their containing rule book
  const readOnly = !hasPermission(ruleBook, RuleBookPermissions.update);
  const canView = hasPermission(ruleBook, RuleBookPermissions.read);
  const hasDeletePermission = hasPermission(
    ruleBook,
    RuleBookPermissions.delete,
  );

  const masterRules =
    masterRulesByMasterRuleBookIdLookup[masterRule.rule_book_id] || [];

  // the number of other rules in this rule book, which are from the same master rule book as the selected rule
  const ruleBookManagedRuleCount =
    rulesBookRules.filter(rule =>
      masterRules.find(masterRule => masterRule.id === rule.master_rule_id),
    ).length || 0;

  const isManagedRule = Boolean(data.rule.master_rule_id);

  const masterRuleBookName = masterRuleBook?.name;
  const hasEditRuleSetPermission = hasPermission(
    ruleSet,
    RuleSetPermissions.update,
  );

  function onClickEditMasterRule() {
    openModalLink(ModalTypes.EditMasterRule, {
      masterRuleId: data.masterRule.id,
    });
  }

  function onClickEdit() {
    dispatch(
      openDispatchingModal(
        ModalTypes.EditRule,
        window.location.hash,
        { ruleId },
        { ruleId },
      ),
    );
  }

  function onClickEditRuleSet(isManagedRule) {
    dispatch(
      openDispatchingModal(ModalTypes.EditRuleSet, window.location.hash, {
        ruleSetId,
        isManagedRule,
      }),
    );
  }

  function onClickAddToNewRuleSet() {
    dispatch(
      openDispatchingModal(ModalTypes.EditRuleSet, window.location.hash, {
        ruleId,
        newRuleSet: {
          criteria: [],
          id: v4(),
          input_field: null,
          name: "",
        },
      }),
    );
  }

  function onClickCopy() {
    dispatch(RuleAction.copy(ruleId));
  }

  function onClickConfirmDelete() {
    dispatch(RuleAction.delete(ruleId));
    dispatch(closeConfirmModal());
  }

  function onClickCancelDelete() {
    dispatch(closeConfirmModal());
  }

  function onClickDelete() {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: "Are you sure you want to delete the Rule?",
        actions: [
          {
            label: "Cancel",
            onClick: onClickCancelDelete,
            secondary: true,
          },
          {
            label: "Delete Rule",
            onClick: onClickConfirmDelete,
            secondary: false,
          },
        ],
      }),
    );
  }

  function onClickConfirmUnsubscribe() {
    dispatch(
      RuleBookAction.unsubscribeFromMasterRuleBook({
        id: rule.rule_book_id,
        masterRuleBookId: masterRule.rule_book_id,
      }),
    );

    dispatch(closeConfirmModal());
  }

  function onClickCancelUnsubscribe() {
    dispatch(closeConfirmModal());
  }

  function onClickUnsubscribe() {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: `Are you sure you want to remove this and ${ruleBookManagedRuleCount} other ${pluralize(
          "rule",
          ruleBookManagedRuleCount,
        )} from the "${masterRuleBookName}" Master Rule Book?`,
        actions: [
          {
            label: "Cancel",
            onClick: onClickCancelUnsubscribe,
            secondary: true,
          },
          {
            label: "Unsubscribe from Master Rule Book",
            onClick: onClickConfirmUnsubscribe,
            secondary: false,
          },
        ],
      }),
    );
  }

  function onClickUpdateRulebook() {
    dispatch(
      RuleBookAction.synchronizeWithMasterRuleBook({
        id: rule.rule_book_id,
        masterRuleBookId: masterRule.rule_book_id,
      }),
    );
  }

  function onClickMoveToExistingRuleSet() {
    dispatch(
      openDispatchingModal(
        ModalTypes.MoveToExistingRuleSet,
        window.location.hash,
        {
          ruleIds: [ruleId],
        },
      ),
    );
  }

  function onClickEditMasterRuleSet() {
    dispatch(
      openDispatchingModal(ModalTypes.EditMasterRuleSet, window.location.hash, {
        masterRuleSetId: masterRule.rule_set_id,
      }),
    );
  }

  const buttons = [
    {
      dataTour: `edit:${rule.name}`,
      default: true,
      icon: readOnly ? faEye : faFileEdit,
      isDisabled: !canView,
      onClick: onClickEdit,
      title: readOnly ? "View" : isManagedRule ? "Customise" : "Edit",
    },
    !isManagedRule && {
      dataTour: "copyRule",
      default: false,
      icon: faCopy,
      isDisabled: readOnly,
      onClick: onClickCopy,
      title: "Copy",
    },
    !isManagedRule && {
      dataTour: "deleteRule",
      default: false,
      icon: faTrash,
      isDisabled: !hasDeletePermission,
      onClick: onClickDelete,
      title: "Delete",
    },

    !isInEditRuleSetModal &&
      ruleSetId && {
        dataTour: "editRuleSet",
        default: false,
        icon: hasEditRuleSetPermission ? faFileEdit : faEye,
        isDisabled: readOnly,
        onClick: () => onClickEditRuleSet(isManagedRule),
        title: `${hasEditRuleSetPermission ? "Edit" : "View"} Rule Set`,
      },

    !isManagedRule &&
      ruleSetOptions.length && {
        dataTour: "addToExistingRuleSet",
        default: false,
        icon: faFileEdit,
        isDisabled: readOnly,
        onClick: onClickMoveToExistingRuleSet,
        title: `${rule.rule_set_id ? "Move" : "Add"} to Existing Rule Set`,
      },
    !isManagedRule && {
      dataTour: "addToNewRuleSet",
      default: false,
      icon: faFileEdit,
      isDisabled: readOnly,
      onClick: onClickAddToNewRuleSet,
      title: `${rule.rule_set_id ? "Move" : "Add"} to New Rule Set`,
    },
    isManagedRule && {
      dataTour: "unsubscribe",
      default: false,
      icon: faSignOut,
      isDisabled: readOnly,
      onClick: onClickUnsubscribe,
      title: "Unsubscribe",
    },
    isManagedRule && {
      dataTour: "updateRuleBook",
      default: false,
      icon: faSync,
      isDisabled: readOnly,
      onClick: onClickUpdateRulebook,
      title: `Synchronise with ${masterRuleBookName}`,
    },
    masterRule &&
      isStaff && {
        dataTour: "editMasterRule",
        default: false,
        icon: faFileEdit,
        isDisabled: readOnly || !isManagedRule,
        onClick: onClickEditMasterRule,
        title: `Edit Master Rule (Staff Only)`,
      },
    isManagedRule &&
      isStaff &&
      masterRule.rule_set_id && {
        dataTour: "editMasterRuleSet",
        default: false,
        icon: faFileEdit,
        isDisabled: readOnly,
        onClick: onClickEditMasterRuleSet,
        title: "Edit Master Rule Set (Staff Only)",
      },
  ].filter(Boolean);

  return <MultiButton buttons={buttons} ButtonComponent={SlimButton} />;
}
